import React, { useContext, useEffect, useMemo, useState } from 'react'
import { get, uniq } from 'lodash'
import Dinero from 'dinero.js'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  Archive as ArchiveIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material'
import { Grid, Stack } from '@mui/material'
import { makeStyles } from '@mui/styles'

import {
  useArchiveSalesOrderMutation,
  useGetSalesOrdersCsvLazyQuery,
  useUploadSalesOrdersCsvMutation,
} from 'services/salesOrders'
import {
  useArchivePurchaseOrderMutation,
  useGetPurchaseOrdersCsvLazyQuery,
  useUploadPurchaseOrdersCsvMutation,
} from 'services/purchaseOrders'

import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

import { calculateTotalValue, calculateTotalWeight, capAllWords } from 'utils/data-manipulation/common'
import { getColumns } from 'components/generic/datagrid/utils'
import {
  CHURN_ZERO_EVENTS as cz,
  CONTRACT_STATUSES,
  CURRENCY_PREPENDS,
  DECIMAL_PRECISION_PRESETS,
  MEASURE_UNIT,
} from 'utils/constants'
import { convertUM, dispatchSnackbar, setDecimalPrecision } from 'utils/helpers'
import { useSelectFilter } from 'utils/custom-hooks/useSelectFilter'
import { capitalize, dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { trackEvent } from 'utils/tracking'
import { AppAction, CONTRACT_ACTIONS } from 'utils/ui/actions'
import { useUploadDownloadCsv } from 'utils/custom-hooks/useUploadDownloadCsv'
import { contractOrderFields } from './contractsHelpers'
import useRbac from '../../../../utils/custom-hooks/useRbac'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-cell[data-field="__check__"]': {
      paddingLeft: '0!important',
    },
  },
}))

interface ContractsContainerProps {
  isSales?: boolean
  data?: any[]
  isLoading?: boolean
  setIsArchiving?: (val: boolean) => void
}

const ContractsContainer: React.FC<ContractsContainerProps> = ({
  isSales = false,
  data = [],
  isLoading = false,
  setIsArchiving = () => {},
}) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getAuthorizedActions } = useRbac()

  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)

  const contractTypePrefix = isSales ? 'sales' : 'purchase'

  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])

  const { defaultCurrency, defaultYardUm, settings } = useAppSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}
  const { currencyExchangeRatesFF } = useAppSelector(state => state.featureFlags?.variables || {})

  const { allowedOptions, renderSelectFilter } = useSelectFilter({
    localStorageKey: 'contractOrdersFilter',
    options: [CONTRACT_STATUSES.OPEN, CONTRACT_STATUSES.CLOSED],
    defaultOptions: [CONTRACT_STATUSES.OPEN.id],
    label: 'Status',
  })

  const {
    isLoading: isLoadingCsv,
    handleUploadButtonClick,
    handleDownloadButtonClick,
    renderFileUploadInput,
  } = useUploadDownloadCsv({
    useGetCsvLazyQuery: isSales ? useGetSalesOrdersCsvLazyQuery : useGetPurchaseOrdersCsvLazyQuery,
    useUploadCsvMutation: isSales ? useUploadSalesOrdersCsvMutation : useUploadPurchaseOrdersCsvMutation,
    fileType: `${contractTypePrefix}-orders`,
  })

  const handleRowClick = row => {
    navigate(`/app/contracts/${contractTypePrefix}-orders/${row.id}`)
  }

  const transformedRows = useMemo(
    () =>
      (data || [])
        .slice()
        .sort((a, b) => (new Date(b?.updatedAt) as any) - (new Date(a?.updatedAt) as any))
        .map(ticket => {
          const ticketCurrency = ticket?.currency
          const ticketPricePrepend =
            !ticketCurrency || defaultCurrency === ticketCurrency
              ? CURRENCY_PREPENDS[defaultCurrency]?.hc
              : CURRENCY_PREPENDS[ticketCurrency]?.fc
          const materials = ticket.priceSheet?.entriesObj || []

          /**
           * Checks if all quantities of materials are either zeros or null.
           * @returns {boolean} Returns true if all quantities are zeros or null, false otherwise.
           */
          const areAllQuantitiesZerosOrNull = () => {
            if (materials.length < 2) return false
            return !materials.some(material => material.targetWeight !== 0 && material.targetWeight !== null)
          }
          let aggregateFields
          if (uniq(materials.map(m => m.weightUnits?.toUpperCase())).length > 1 || areAllQuantitiesZerosOrNull()) {
            aggregateFields = {
              price: 'Mixed',
              um: 'Mixed',
              ordered: 'Mixed',
              fulfilled: 'Mixed',
              remain: 'Mixed',
            }
          } else {
            const totalWeight = calculateTotalWeight(materials)
            const um = (materials?.[0]?.weightUnits || defaultYardUm).toUpperCase()
            const convertedFulfilled = convertUM(ticket.fulfilledAmount, defaultYardUm, um)
            let price = 0
            if (materials?.length === 1) {
              const matPrice = get(materials[0], `${isSales ? 'sell' : 'buy'}Price.price`) || 0
              price = parseFloat(typeof matPrice === 'object' ? Dinero(matPrice).toUnit() : matPrice) || 0
            } else {
              price =
                calculateTotalValue(materials, {
                  pricePath: `${isSales ? 'sell' : 'buy'}Price.price`,
                }) / totalWeight || 0
            }

            aggregateFields = {
              price: `${
                currencyExchangeRatesFF ? ticketPricePrepend : '$'
              }${setDecimalPrecision(price)}/${disableUmDropdowns ? defaultYardUm : um}`,
              um,
              ordered: `${setDecimalPrecision(
                totalWeight,
                DECIMAL_PRECISION_PRESETS.WEIGHT,
              )} ${disableUmDropdowns ? defaultYardUm : um}`,
              fulfilled: `${setDecimalPrecision(
                convertedFulfilled,
                DECIMAL_PRECISION_PRESETS.WEIGHT,
              )} ${disableUmDropdowns ? defaultYardUm : um}`,
              remain: `${setDecimalPrecision(
                totalWeight - convertedFulfilled,
                DECIMAL_PRECISION_PRESETS.WEIGHT,
              )} ${disableUmDropdowns ? defaultYardUm : um}`,
            }
          }

          return {
            id: ticket.id,
            readableId: ticket[`${contractTypePrefix}OrderTicketId`],
            contractOrderNumber: ticket[`${contractTypePrefix}OrderTicketId`],
            customerOrderNumber: ticket[`customer${isSales ? 'P' : 'S'}ONumber`],
            customerName: ticket[`${isSales ? 'customer' : 'supplier'}`].displayName,
            effectiveDate: dateFormatter(ticket[`order${isSales ? 'SalesDetails' : ''}`].effectiveDate),
            expirationDate: dateFormatter(ticket[`order${isSales ? 'SalesDetails' : ''}`].expirationDate),
            ...(isSales && {
              freightDestination: ticket?.freightDestination || '',
            }),
            pickupDelivery: capAllWords(ticket[`order${isSales ? 'SalesDetails' : ''}`].pickupDelivery || ''),
            materials,
            materialsText: materials.map(material => material.materialName || '').join(', '),
            ...aggregateFields,
            status: ticket.status,
          }
        }),
    [data, defaultYardUm],
  )

  const columns = [
    'contractOrderNumber',
    'customerOrderNumber',
    'customerName',
    ...(isSales ? ['freightDestination'] : []),
    'effectiveDate',
    'expirationDate',
    ...(!isSales ? ['pickupDelivery'] : []),
    'materials',
    'price',
    'ordered',
    'fulfilled',
    'remain',
    'status',
  ]

  useEffect(() => {
    const csvActions: AppAction[] = [
      {
        icon: <CloudUploadIcon />,
        text: `Upload ${capitalize(contractTypePrefix)} Orders`,
        id: `upload-${contractTypePrefix}-orders`,
        variant: 'text',
        onClick: handleUploadButtonClick,
        actionPath: `contracts/${contractTypePrefix}-orders`,
      },
      {
        icon: <CloudDownloadIcon />,
        text: `Download ${capitalize(contractTypePrefix)} Orders`,
        id: `download-${contractTypePrefix}-orders`,
        variant: 'text',
        onClick: handleDownloadButtonClick,
        actionPath: `contracts/${contractTypePrefix}-orders`,
      },
    ]

    if (typeof contentContext?.setContainerTitle === 'function') {
      contentContext.setContainerTitle('Contracts')
    }

    if (typeof tabsContext?.setContainerTabsVisibility === 'function') {
      tabsContext.setContainerTabsVisibility(true)
    }

    const pageActions = [...csvActions, ...CONTRACT_ACTIONS]
    // ensure we only show actions that the user is authorized to see
    const authzdActions = getAuthorizedActions(pageActions)
    contentContext.setContainerActions(authzdActions)
  }, [contractTypePrefix])

  const actionButtons = [
    {
      text: 'Archive',
      variant: 'outlined',
      icon: <ArchiveIcon />,
      id: `archive-${isSales ? 'sales-' : 'purchase-'}-orders-button`,
      onClick: () => setShowArchiveModal(true),
    },
  ]

  const [archiveSalesOrder, { isLoading: isLoadingArchiveSalesOrder }] = useArchiveSalesOrderMutation()
  const [archivePurchaseOrder, { isLoading: isLoadingArchivePurchaseOrder }] = useArchivePurchaseOrderMutation()

  const isLoadingOrders = isLoading || isLoadingArchivePurchaseOrder || isLoadingArchiveSalesOrder || isLoadingCsv

  const archiveSelectedOrders = async () => {
    setIsArchiving(true)
    setShowArchiveModal(false)

    const successfullyArchivedOrders: any = {}
    const promises: Promise<any>[] = []

    selectedOrders.forEach(orderId => {
      successfullyArchivedOrders[orderId] = false
      if (isSales) {
        promises.push(archiveSalesOrder({ salesOrderId: orderId }))
      } else {
        promises.push(archivePurchaseOrder({ purchaseOrderId: orderId }))
      }
    })

    let hasBusinessRuleViolations = false
    let hasOtherErrors = false

    const responses = await Promise.all(promises)
    setIsArchiving(false)

    responses.forEach((response: any) => {
      // error
      if (response.error) {
        if (response.error?.data?.code === 'BUSINESS_RULE_VIOLATION') {
          hasBusinessRuleViolations = true
        } else {
          hasOtherErrors = true
        }
        return
      }

      // success
      successfullyArchivedOrders[response?.data?.id] = true
    })

    // snackbar messaging
    const eventName = isSales ? cz.ARCHIVE_SALES_ORDER : cz.ARCHIVE_PURCHASE_ORDER
    if (hasBusinessRuleViolations) {
      const errorMessage = `Some of the selected orders have ${isSales ? 'outbound' : 'inbound'} tickets attached and cannot be archived`
      trackEvent({
        eventName,
        errorMsg: errorMessage,
      })
      dispatchSnackbar(dispatch, 'error', errorMessage)
    } else if (hasOtherErrors) {
      const errorMessage = 'There was an error archiving some of the selected orders'
      trackEvent({
        eventName,
        errorMsg: errorMessage,
      })
      dispatchSnackbar(dispatch, 'error', errorMessage)
    } else {
      trackEvent(eventName)
      dispatchSnackbar(dispatch, 'success', 'Selected orders have been successfully archived')
    }

    // remove successfully archived orders from selectedOrders
    const remainingOrderIds = Object.keys(successfullyArchivedOrders).filter(
      orderId => !successfullyArchivedOrders[orderId],
    )
    setSelectedOrders(remainingOrderIds)
  }

  return (
    <Stack>
      {renderFileUploadInput()}
      <AlertModal
        open={showArchiveModal}
        modalType="void"
        title={`Archive ${isSales ? 'Sales' : 'Purchase'} Orders`}
        question={`Are you sure you want to archive ${
          selectedOrders.length > 1 ? 'these' : 'this'
        } ${isSales ? 'Sales' : 'Purchase'} ${selectedOrders.length === 1 ? 'Order' : 'Orders'}?`}
        description="Archiving is irreversible and you will need to contact GreenSpark support to undo this action."
        confirmLabel="YES, CONFIRM ARCHIVE"
        cancelLabel="GO BACK"
        onClose={() => setShowArchiveModal(false)}
        onSubmit={archiveSelectedOrders}
      />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <DataGridWithSearchV2
          className={classes.root}
          rows={transformedRows}
          loading={isLoadingOrders}
          selectFilterPath="status"
          allowedOptions={allowedOptions}
          renderSelectFilter={renderSelectFilter}
          columns={getColumns(columns, contractOrderFields(isSales))}
          textSearchPlaceholder={`Search ${isSales ? 'Sales' : 'Purchase'} Orders`}
          onRowClick={handleRowClick}
          centerContent={selectedOrders?.length ? `${selectedOrders?.length} selected` : ''}
          checkboxSelection
          actionButtons={selectedOrders?.length ? actionButtons : []}
          selectionModel={selectedOrders}
          onSelectionModelChange={selectionModel => {
            setSelectedOrders(selectionModel)
          }}
          keepNonExistentRowsSelected
          autoPageSize={false}
        />
      </Grid>
    </Stack>
  )
}

export default ContractsContainer
